import Link from 'components/ui/AppLink';
import Loader from 'components/ui/LoadingIndicator';
import { MessageView } from 'components/ui/Feedback';

import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { GET_CHANNEL_WITH_CATEGORIES_LEAN } from '../../../gql/channels';
import { NON_CHARGE_CHANNEL_SUBSCRIPTION_EXPIRED_ACKNOWLEDGED } from '../../../gql/subscriptions';
import { getDisplayProperties } from '../../../shared/channelSubscription';
import {
    Channel,
    ChannelState,
    ChannelSubscription,
} from '../../../shared/types';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { formatDisplayDate } from '../../../utils/date.utils';
import { MessageTypes } from '../../providers/NotificationProvider';
import PremiumChannelList from '../../ui/PremiumChannelList';
import BrowseArticleList from './BrowseArticleList';
import ChannelChartsArticles from './ChannelChartsArticles';
import ChannelCover from './ChannelCover';
import { getChannelSubscriptionLink } from '../../../utils/link.utils';
import { config } from '../../../config';
import { isSubscriptionExpired } from '../../../utils/channelSubscriptions.utils';
import { ArticleLinkList } from '../../ui/ArticleLinkList';
import { GET_STATS_BY_CHANNEL } from '../../../gql/articles';
import { isValidMongoId } from '../../../utils/id-validation.utils';

import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import { Grid } from '@mui/material';
import moment from 'moment';
import { AlertBox } from 'components/ui/Feedback';

const ChannelPage: React.FC = () => {
    const { push } = useHistory();
    const { channelId } = useParams<{ channelId: string }>();
    const isValidChannelId = isValidMongoId(channelId);

    const errorMessage = (
        <MessageView
            styleName="primary"
            size="large"
            title="Channel not found"
            primaryAction={{
                label: 'Return to Neon Insights',
                onClick: () => push('/'),
            }}
        ></MessageView>
    );

    const [
        pendingSubscriptionMessageVisible,
        setPendingSubscriptionMessageVisible,
    ] = useState<boolean>(true);
    const [
        paymentFailedSubscriptionMessageVisible,
        setPaymentFailedSubscriptionMessageVisible,
    ] = useState<boolean>(true);
    const [expiryBannerAlertVisible, setExpiryBannerAlertVisible] =
        useState<boolean>(true);
    const [trialExpiryAlertVisible, setTrialExpiryAlertVisible] =
        useState<boolean>(true);
    const oneMonthAgo = moment().utc(false).subtract(1, 'month').startOf('day');

    const [getStatsByChannelQuery, { data: articleData }] = useLazyQuery(
        GET_STATS_BY_CHANNEL,
        { fetchPolicy: 'network-only' }
    );

    const [
        getChannelWithCategoryQuery,
        { loading, data: channelWithCategoriesData },
    ] = useLazyQuery<{ channel: Channel }>(GET_CHANNEL_WITH_CATEGORIES_LEAN, {
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (isValidChannelId) {
            getStatsByChannelQuery({
                variables: {
                    query: {
                        channelId: channelId,
                        from: oneMonthAgo,
                    },
                },
            });
        }
    }, []);

    useEffect(() => {
        if (isValidChannelId) {
            getChannelWithCategoryQuery({
                variables: { id: channelId },
            });
        }
    }, []);

    const [acknowledgedNonChargeSubExpired] = useMutation(
        NON_CHARGE_CHANNEL_SUBSCRIPTION_EXPIRED_ACKNOWLEDGED,
        {
            variables: { channelId: channelId },
            refetchQueries: [
                {
                    query: GET_CHANNEL_WITH_CATEGORIES_LEAN,
                    variables: { id: channelId },
                },
            ],
        }
    );

    const channelWithCategories: Channel | undefined =
        channelWithCategoriesData?.channel;
    const userChannelSubscription: ChannelSubscription | undefined =
        channelWithCategories?.userChannelSubscription;

    useEffect(() => {
        if (isValidChannelId) {
            if (channelWithCategories?.state === ChannelState.Archived) {
                push(baseUrls.channels.main);
            }
        }
    }, [channelWithCategories, push]);

    if (!isValidChannelId) return errorMessage;

    if (loading || !channelWithCategoriesData || !userChannelSubscription)
        return <Loader />;

    const {
        nonChargeSubscriptionExpired,
        cancelAtPeriodEnd,
        paymentFailedAlertVisible,
        isTrialSubscriptionActive,
        isTrialSubscriptionExpired,
        isTrialSubscriptionPending,
        isPremiumChannel,
    } = getDisplayProperties(userChannelSubscription, channelWithCategories);

    const setExpiredBannerAcknowledged = () => {
        isValidChannelId &&
            nonChargeSubscriptionExpired &&
            acknowledgedNonChargeSubExpired({
                variables: { channelId: channelId },
            });
        setExpiryBannerAlertVisible(false);
    };

    const showExpiryBanner =
        userChannelSubscription.cancelAt &&
        isSubscriptionExpired(userChannelSubscription);
    const showPendingCancellationBanner =
        userChannelSubscription.cancelAt &&
        !isSubscriptionExpired(userChannelSubscription) &&
        moment(userChannelSubscription.cancelAt).diff(moment(), 'days') <
            config.numberOfDaysToShowExpiryBanner;

    const dateOfCancellation = formatDisplayDate(
        userChannelSubscription.cancelRequestedAt,
        false
    );
    const lastDateOfAccess = formatDisplayDate(
        userChannelSubscription.cancelAt,
        false
    );

    return (
        <>
            <Grid container spacing={2}>
                {isPremiumChannel && (
                    <>
                        {showExpiryBanner || nonChargeSubscriptionExpired ? (
                            <Grid size={12}>
                                <AlertBox
                                    id="channel-page-expired-noncharge-subscription-alert"
                                    visible={expiryBannerAlertVisible}
                                    children={
                                        <>
                                            Your subscription to this channel
                                            expired on {lastDateOfAccess}. You
                                            may resubscribe below.
                                        </>
                                    }
                                    styleName={MessageTypes.warning}
                                    onClose={() =>
                                        setExpiredBannerAcknowledged()
                                    }
                                />
                            </Grid>
                        ) : null}
                        {isTrialSubscriptionActive ? (
                            <Grid size={12}>
                                <AlertBox
                                    id="channel-page-expired-trial-subscription-alert"
                                    visible={trialExpiryAlertVisible}
                                    children={
                                        <>
                                            You are on a free trial of this
                                            channel that expires on{' '}
                                            {lastDateOfAccess}. Add payment
                                            details to avoid an interruption of
                                            your access.
                                        </>
                                    }
                                    styleName={MessageTypes.info}
                                    onClose={() =>
                                        setTrialExpiryAlertVisible(false)
                                    }
                                />
                            </Grid>
                        ) : null}
                        {!isTrialSubscriptionActive &&
                        cancelAtPeriodEnd &&
                        !showPendingCancellationBanner &&
                        !isSubscriptionExpired(userChannelSubscription) ? (
                            <Grid size={12}>
                                <AlertBox
                                    id="channel-page-pending-cancellation-alert"
                                    visible={pendingSubscriptionMessageVisible}
                                    children={
                                        <>
                                            You cancelled your subscription on{' '}
                                            {dateOfCancellation}. Your access
                                            expires on {lastDateOfAccess}.
                                        </>
                                    }
                                    styleName={MessageTypes.info}
                                    onClose={() =>
                                        setPendingSubscriptionMessageVisible(
                                            false
                                        )
                                    }
                                />
                            </Grid>
                        ) : null}
                        {!isTrialSubscriptionActive &&
                        showPendingCancellationBanner ? (
                            <Grid size={12}>
                                <AlertBox
                                    id="channel-page-pending-cancellation-alert"
                                    visible={pendingSubscriptionMessageVisible}
                                    children={
                                        <>
                                            You do not have a billing agreement
                                            set up for access to this Premium
                                            Channel. Your access expires on{' '}
                                            {lastDateOfAccess}. Please choose a
                                            renewal option in settings.
                                        </>
                                    }
                                    styleName={MessageTypes.info}
                                    onClose={() =>
                                        setPendingSubscriptionMessageVisible(
                                            false
                                        )
                                    }
                                />
                            </Grid>
                        ) : null}
                        {paymentFailedAlertVisible ? (
                            <Grid size={12}>
                                <AlertBox
                                    id="channel-page-payment-failed-alert"
                                    visible={
                                        paymentFailedSubscriptionMessageVisible
                                    }
                                    children={
                                        <>
                                            There was an issue processing your
                                            payment for this channel. Please
                                            update your payment details to avoid
                                            cancellation.
                                            <Link
                                                to={`${baseUrls.subscriptions}?channels`}
                                            >
                                                <b> Update billing details.</b>
                                            </Link>
                                        </>
                                    }
                                    styleName={MessageTypes.warning}
                                    onClose={() =>
                                        setPaymentFailedSubscriptionMessageVisible(
                                            false
                                        )
                                    }
                                />
                            </Grid>
                        ) : null}
                    </>
                )}
                <Grid size={12}>
                    <Grid container spacing={4}>
                        <Grid size={12}>
                            <ChannelCover
                                channel={channelWithCategories as Channel}
                                disclaimerText={
                                    isTrialSubscriptionActive ? (
                                        `Your free trial access ends on ${formatDisplayDate(userChannelSubscription.cancelAt, false)}`
                                    ) : isTrialSubscriptionPending ? (
                                        <>
                                            Don't want to wait?{' '}
                                            <Link
                                                to={getChannelSubscriptionLink(
                                                    channelId
                                                )}
                                            >
                                                Buy now
                                            </Link>
                                        </>
                                    ) : isTrialSubscriptionExpired ? (
                                        ''
                                    ) : undefined
                                }
                            />
                        </Grid>
                        <Grid
                            id="articles-list"
                            size={{
                                xs: 12,
                                md: 8,
                            }}
                        >
                            <Grid container spacing={4}>
                                <Grid size={12}>
                                    <ChannelChartsArticles />
                                </Grid>
                                <Grid size={12}>
                                    <BrowseArticleList
                                        title="Latest articles"
                                        premiumChannelId={channelId}
                                        showPlacement
                                        showFilter
                                        pinnedFirst
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid
                            sx={(theme) => ({
                                [theme.breakpoints.down('sm')]: { mt: 4 },
                                [theme.breakpoints.up('md')]: {
                                    pl: `${theme.spacing(5)} !important`,
                                },
                                [theme.breakpoints.up('lg')]: {
                                    pl: `${theme.spacing(9)} !important`,
                                },
                                '& > div': { mb: 6 },
                            })}
                            size={{
                                xs: 12,
                                md: 4,
                            }}
                        >
                            <ArticleLinkList
                                title="Trending in this Channel"
                                articles={
                                    articleData?.getStatsByChannel
                                        ?.trendingArticles
                                }
                            />
                            <PremiumChannelList
                                maxLength={25}
                                channelIdToExcludeFromList={channelId}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default ChannelPage;
